// FieldDesk web-dispatcher — HTTP-клиент.
// Базовый URL в dev проксируется Vite (`/api` → localhost:3000).
// JWT хранится в AuthContext и передаётся через getToken-колбэк.

import type {
  ActivationCodeResponse,
  AdminSettings,
  AdminUserRow,
  AnalyticsResponse,
  BitrixAuthParams,
  BitrixAuthResponse,
  BitrixDealContext,
  BitrixSelectItem,
  ClientDto,
  ClientsListResponse,
  ClientVisitHistoryRow,
  CreateVisitInput,
  DashboardResponse,
  DayOffDto,
  DealSearchItem,
  EngineerListItem,
  GeocodeForwardResult,
  GeocodeReverseResult,
  ReportSummary,
  ReportVisitsResponse,
  RoutesResponse,
  UserRole,
  VisitDto,
  VisitStatus,
  VisitType,
  VisitsListResponse,
  VisitWithEvents,
} from './types';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');
const API_PREFIX = '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;
  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiClientOptions {
  getToken(): string | null;
  onUnauthorized?(err: ApiError): void;
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const url = `${API_BASE}${API_PREFIX}${path}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  private async request<T>(
    method: HttpMethod,
    path: string,
    init: { body?: unknown; params?: Record<string, string | number | undefined>; auth?: boolean } = {},
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (init.body !== undefined) headers['Content-Type'] = 'application/json';

    const useAuth = init.auth !== false;
    if (useAuth) {
      const token = this.opts.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    let res: Response;
    try {
      res = await fetch(buildUrl(path, init.params), {
        method,
        headers,
        body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      });
    } catch (e) {
      throw new ApiError(0, `Сеть недоступна: ${(e as Error).message}`, 'NETWORK');
    }

    const text = await res.text();
    const json: unknown = text ? safeJson(text) : null;

    if (!res.ok) {
      const err = toApiError(res.status, json);
      if (useAuth && err.status === 401) this.opts.onUnauthorized?.(err);
      throw err;
    }
    return json as T;
  }

  // --- auth ---
  async loginViaBitrix(params: BitrixAuthParams): Promise<BitrixAuthResponse> {
    return this.request('POST', '/auth/bitrix', { body: params, auth: false });
  }

  // --- visits ---
  async listVisits(params?: {
    from?: string;
    to?: string;
    status?: VisitStatus;
    engineerId?: number;
    clientId?: number;
    bitrixDealId?: string;
    limit?: number;
    offset?: number;
  }): Promise<VisitsListResponse> {
    return this.request('GET', '/visits', { params });
  }

  async getVisit(id: number): Promise<VisitWithEvents> {
    return this.request('GET', `/visits/${id}`);
  }

  async createVisit(input: CreateVisitInput): Promise<VisitDto> {
    return this.request('POST', '/visits', { body: input });
  }

  async cancelVisit(id: number, reason: string): Promise<VisitDto> {
    return this.request('POST', `/visits/${id}/cancel`, { body: { reason } });
  }

  /** PATCH /visits/:id — перенос даты/времени, переназначение инженера, описание. */
  async updateVisit(
    id: number,
    input: {
      engineerId?: number;
      scheduledDate?: string;
      scheduledStart?: string;
      scheduledEnd?: string;
      description?: string;
      /** Причина переноса — в историю, уведомление инженеру и Б24. */
      rescheduleReason?: string;
    },
  ): Promise<VisitDto> {
    return this.request('PATCH', `/visits/${id}`, { body: input });
  }

  async updateVisitStatus(id: number, status: VisitStatus): Promise<VisitDto> {
    return this.request('PATCH', `/visits/${id}/status`, { body: { status } });
  }

  // --- clients ---
  async listClients(params?: { search?: string; storeType?: string; status?: string; limit?: number }): Promise<ClientsListResponse> {
    return this.request('GET', '/clients', { params });
  }

  async getClient(id: number): Promise<ClientDto> {
    return this.request('GET', `/clients/${id}`);
  }

  async updateClientStatus(id: number, status: 'active' | 'passive'): Promise<ClientDto> {
    return this.request('PATCH', `/clients/${id}`, { body: { status } });
  }

  async listClientVisits(id: number): Promise<{ items: ClientVisitHistoryRow[] }> {
    return this.request('GET', `/clients/${id}/visits`);
  }

  // --- engineers ---
  async listEngineers(date?: string): Promise<EngineerListItem[]> {
    return this.request('GET', '/engineers', { params: date ? { date } : undefined });
  }

  // --- clients / deal context ---
  async getBitrixDealContext(dealId: string): Promise<BitrixDealContext> {
    return this.request('GET', `/bitrix/deals/${dealId}/context`);
  }

  async searchBitrixDeals(query: string, limit = 20): Promise<{ items: DealSearchItem[] }> {
    return this.request('GET', '/bitrix/deals/search', { params: { q: query, limit } });
  }

  // --- geocode (proxy to Nominatim) ---
  async geocodeReverse(lat: number, lng: number): Promise<GeocodeReverseResult> {
    return this.request('GET', '/geocode/reverse', { params: { lat, lng } });
  }

  async geocodeForward(q: string): Promise<GeocodeForwardResult> {
    return this.request('GET', '/geocode/forward', { params: { q } });
  }

  // --- reports ---
  async getReportSummary(): Promise<ReportSummary> {
    return this.request('GET', '/reports/summary');
  }

  async getReportDashboard(period: 'week' | 'month'): Promise<DashboardResponse> {
    return this.request('GET', '/reports/dashboard', { params: { period } });
  }

  async getReportVisits(params: {
    from: string;
    to: string;
    engineerId?: number;
    types?: VisitType[];
  }): Promise<ReportVisitsResponse> {
    return this.request('GET', '/reports/visits', {
      params: {
        from: params.from,
        to: params.to,
        engineerId: params.engineerId,
        types: params.types && params.types.length > 0 ? params.types.join(',') : undefined,
      },
    });
  }

  async getReportAnalytics(params: {
    from: string;
    to: string;
    engineerId?: number;
    types?: VisitType[];
  }): Promise<AnalyticsResponse> {
    return this.request('GET', '/reports/analytics', {
      params: {
        from: params.from,
        to: params.to,
        engineerId: params.engineerId,
        types: params.types && params.types.length > 0 ? params.types.join(',') : undefined,
      },
    });
  }

  async getReportRoutes(params: { from: string; to: string; engineerId?: number }): Promise<RoutesResponse> {
    return this.request('GET', '/reports/routes', { params });
  }

  // --- bitrix sync (ADMIN) ---
  async syncUsersFromBitrix(): Promise<{ ok: boolean; report: unknown }> {
    return this.request('POST', '/bitrix/sync-users');
  }

  // --- activation codes (ADMIN / DISPATCHER) ---
  async createActivationCode(userId: number, expiresInHours?: number): Promise<ActivationCodeResponse> {
    return this.request('POST', '/activation-codes', {
      body: expiresInHours ? { userId, expiresInHours } : { userId },
    });
  }

  // --- user lifecycle (ADMIN) ---
  async deactivateUser(userId: number): Promise<{ id: number; activationStatus: string }> {
    return this.request('PATCH', `/users/${userId}/deactivate`);
  }

  async reactivateUser(userId: number): Promise<{ id: number; activationStatus: string }> {
    return this.request('PATCH', `/users/${userId}/activate`);
  }

  async deleteSeedEngineers(): Promise<{ deleted: number }> {
    return this.request('DELETE', '/users/seed-engineers');
  }

  // --- user access management (ADMIN) ---
  /** PATCH /api/users/:id — отключить Telegram (→INVITED) или реактивировать DISABLED (→INVITED). */
  async patchUserAccess(
    userId: number,
    data: { telegramUserId?: null; activationStatus?: 'INVITED' },
  ): Promise<{ id: number; activationStatus: string }> {
    return this.request('PATCH', `/users/${userId}`, { body: data });
  }

  // --- visibility (ADMIN) ---
  async updateEngineerVisibility(
    userId: number,
    isVisibleInSchedule: boolean,
  ): Promise<{ id: number; isVisibleInSchedule: boolean }> {
    return this.request('PATCH', `/users/${userId}`, { body: { isVisibleInSchedule } });
  }

  // --- admin: users (SUPER_ADMIN) ---
  async adminListUsers(): Promise<{ items: AdminUserRow[] }> {
    return this.request('GET', '/admin/users');
  }

  async adminSetUserRole(userId: number, role: UserRole): Promise<AdminUserRow> {
    return this.request('PATCH', `/admin/users/${userId}/role`, { body: { role } });
  }

  // --- admin: settings (SUPER_ADMIN) ---
  async adminGetSettings(): Promise<AdminSettings> {
    return this.request('GET', '/admin/settings');
  }

  async adminPatchSettings(data: Partial<AdminSettings>): Promise<AdminSettings> {
    return this.request('PATCH', '/admin/settings', { body: data });
  }

  async adminGetDealCategories(): Promise<{ items: BitrixSelectItem[] }> {
    return this.request('GET', '/admin/bitrix/deal-categories');
  }

  async adminGetDepartments(): Promise<{ items: BitrixSelectItem[] }> {
    return this.request('GET', '/admin/bitrix/departments');
  }

  async adminSyncAllBitrixUsers(): Promise<{ added: number; skipped: number; total: number }> {
    return this.request('POST', '/admin/sync-all-bitrix-users');
  }

  // --- days-off (GET — все роли; POST/DELETE — ADMIN) ---
  async listDaysOff(userId: number, range?: { from?: string; to?: string }): Promise<{ items: DayOffDto[] }> {
    return this.request('GET', `/users/${userId}/days-off`, { params: range });
  }

  async addDayOff(userId: number, date: string, reason?: string): Promise<DayOffDto> {
    return this.request('POST', `/users/${userId}/days-off`, {
      body: reason ? { date, reason } : { date },
    });
  }

  async removeDayOff(userId: number, date: string): Promise<void> {
    return this.request('DELETE', `/users/${userId}/days-off/${date}`);
  }
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return null; }
}

function toApiError(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object') {
    const envelope = body as { error?: { code?: string; message?: string; details?: unknown }; code?: string; message?: string };
    const err = envelope.error ?? envelope;
    const msg = (err.message ?? `HTTP ${status}`) as string;
    return new ApiError(status, msg, err.code, 'details' in err ? err.details : err);
  }
  return new ApiError(status, `HTTP ${status}`);
}
